import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Play, Pause, Ban, Copy as CopyIcon, RotateCcw, Trash2, Send, ExternalLink } from "lucide-react";
import CampaignEditor from "@/components/bulk/CampaignEditor";
import CampaignResendDialog from "@/components/bulk/CampaignResendDialog";

const STATUS_STYLE = {
  draft: "bg-muted text-muted-foreground",
  scheduled: "bg-sun/25 text-yellow-700",
  queued: "bg-sun/25 text-yellow-700",
  running: "bg-emerald-brand/15 text-emerald-brand animate-pulse",
  paused: "bg-yellow-100 text-yellow-700",
  completed: "bg-emerald-brand/15 text-emerald-brand",
  cancelled: "bg-muted text-muted-foreground",
  error: "bg-crimson/12 text-crimson",
};

export default function Campaigns() {
  const { activeCompanyId } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null = closed, {} = new draft, {...} = edit
  const [resendSrc, setResendSrc] = useState(null);

  const load = async () => {
    const params = activeCompanyId ? { company_id: activeCompanyId } : {};
    const { data } = await api.get("/bulk/campaigns", { params });
    setItems(data);
  };
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [activeCompanyId]);

  const action = async (id, act) => {
    try {
      await api.post(`/bulk/campaigns/${id}/${act}`);
      toast.success(`Campaign ${act}ed`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const copy = async (id) => {
    try { const { data } = await api.post(`/bulk/campaigns/${id}/copy`); toast.success("Copied — opening draft"); load(); setTimeout(() => window.location.assign(`/bulk/campaigns/${data.id}`), 300); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this campaign and all its delivery records?")) return;
    try { await api.delete(`/bulk/campaigns/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="campaigns-page">
      <div className="flex justify-end">
        <Button onClick={() => setEditing({})} className="rounded-sm" data-testid="new-campaign-button"><Plus className="h-4 w-4 mr-1.5" /> New campaign</Button>
      </div>
      <div className="border border-border rounded-sm overflow-hidden bg-white">
        <table className="tbl w-full" data-testid="campaigns-table">
          <thead><tr><th>Campaign</th><th>Status</th><th>Progress</th><th>Schedule</th><th></th></tr></thead>
          <tbody>
            {items.map((c) => {
              const done = c.sent + c.failed;
              const pct = c.total ? Math.round((done / c.total) * 100) : 0;
              const st = STATUS_STYLE[c.status] || STATUS_STYLE.draft;
              return (
                <tr key={c.id} data-testid={`campaign-row-${c.id}`}>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="h-7 w-7 rounded-sm bg-emerald-brand/12 text-emerald-brand grid place-items-center"><Send className="h-3.5 w-3.5" /></div>
                      <div>
                        <div className="font-medium">{c.name}</div>
                        <div className="text-[11px] font-mono text-muted-foreground truncate max-w-[300px]">{c.subject}</div>
                      </div>
                    </div>
                  </td>
                  <td><Badge className={`rounded-sm text-[10px] font-mono uppercase ${st}`} data-testid={`campaign-status-${c.id}`}>{c.status}</Badge></td>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden min-w-[80px]">
                        <div className="h-full bg-gradient-to-r from-emerald-brand to-sun" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="font-mono text-[10px] text-muted-foreground whitespace-nowrap">{done}/{c.total}</span>
                    </div>
                  </td>
                  <td className="font-mono text-[10px] text-muted-foreground">{c.schedule_at ? c.schedule_at.slice(0, 16).replace("T", " ") : "—"}</td>
                  <td className="text-right space-x-1 whitespace-nowrap">
                    <Link to={`/bulk/campaigns/${c.id}`}>
                      <Button size="sm" variant="outline" className="rounded-sm text-xs" data-testid={`open-campaign-${c.id}`}><ExternalLink className="h-3.5 w-3.5 mr-1" /> Open</Button>
                    </Link>
                    {["draft", "scheduled", "paused"].includes(c.status) && (
                      <Button size="sm" className="rounded-sm text-xs" onClick={() => action(c.id, "start")} data-testid={`start-campaign-${c.id}`}><Play className="h-3.5 w-3.5" /></Button>
                    )}
                    {c.status === "running" && (
                      <Button size="sm" variant="outline" className="rounded-sm text-xs" onClick={() => action(c.id, "pause")} data-testid={`pause-campaign-${c.id}`}><Pause className="h-3.5 w-3.5" /></Button>
                    )}
                    {["running", "queued", "paused", "scheduled"].includes(c.status) && (
                      <Button size="sm" variant="outline" className="rounded-sm text-xs" onClick={() => action(c.id, "cancel")} data-testid={`cancel-campaign-${c.id}`}><Ban className="h-3.5 w-3.5" /></Button>
                    )}
                    <Button size="sm" variant="ghost" className="text-xs" onClick={() => copy(c.id)} data-testid={`copy-campaign-${c.id}`} title="Duplicate as draft"><CopyIcon className="h-3.5 w-3.5" /></Button>
                    {c.status === "completed" && (
                      <Button size="sm" variant="ghost" className="text-xs text-emerald-brand" onClick={() => setResendSrc(c)} data-testid={`resend-campaign-${c.id}`} title="Resend to same recipients"><RotateCcw className="h-3.5 w-3.5" /></Button>
                    )}
                    <Button size="sm" variant="ghost" className="text-xs text-crimson" onClick={() => remove(c.id)} data-testid={`delete-campaign-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan="5" className="text-center text-muted-foreground py-8">No campaigns yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing !== null && (
        <CampaignEditor initial={editing} onClose={() => setEditing(null)} onSaved={load} />
      )}
      {resendSrc && (
        <CampaignResendDialog campaign={resendSrc} onClose={() => setResendSrc(null)} onDone={load} />
      )}
    </div>
  );
}
